import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@lib/context/AuthContextType';
import { updateClientProfile, updatePortalProfile } from '@lib/api/portal';

interface OnboardingForm {
  name: string;
  company: string;
  phone: string;
  address: string;
  billing_city: string;
  billing_province: string;
  billing_postal: string;
  site_address: string;
  preferred_contact: string;
  how_heard: string;
}

const EMPTY: OnboardingForm = {
  name: '', company: '', phone: '',
  address: '', billing_city: '', billing_province: '', billing_postal: '',
  site_address: '', preferred_contact: '', how_heard: '',
};

const PROVINCES = [
  'AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT',
];

const HOW_HEARD_OPTIONS = [
  'Word of mouth / referral',
  'Google search',
  'Facebook / Instagram',
  'Kijiji / Marketplace',
  'Returning client',
  'Trade show / event',
  'Other',
];

const CONTACT_OPTIONS = [
  { value: 'phone', label: 'Phone call', icon: 'phone' },
  { value: 'email', label: 'Email',      icon: 'email' },
  { value: 'text',  label: 'Text / SMS', icon: 'sms' },
];

const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40 focus:border-[#C77D3F] bg-gray-50';

export const ClientOnboardingPage = () => {
  const { user, setSession } = useContext(AuthContext)!;
  const navigate = useNavigate();
  const [form, setForm] = useState<OnboardingForm>({ ...EMPTY, name: user?.username || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof OnboardingForm, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Please enter your name.'); return; }
    setBusy(true);
    setError('');
    try {
      await updateClientProfile({
        name: form.name.trim(),
        company: form.company.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        billing_city: form.billing_city.trim() || undefined,
        billing_province: form.billing_province || undefined,
        billing_postal: form.billing_postal.trim() || undefined,
        site_address: form.site_address.trim() || undefined,
        preferred_contact: form.preferred_contact || undefined,
        how_heard: form.how_heard || undefined,
        onboarding_complete: true,
      });
      if (form.name.trim() && user) {
        await updatePortalProfile({ username: form.name.trim(), phone: form.phone.trim() || undefined });
        const stored = localStorage.getItem('accessToken');
        const refresh = localStorage.getItem('refreshToken');
        if (stored && refresh) {
          setSession(stored, refresh, { ...user, username: form.name.trim() });
        }
      }
      navigate('/portal/client');
    } catch {
      setError('Failed to save your information. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const skip = () => navigate('/portal/client');

  return (
    <div className="min-h-screen bg-[#FAF6F0] pb-12">
      {/* Header */}
      <div className="bg-[#C77D3F] px-5 py-8 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <span className="material-symbols-outlined text-white text-xl">waving_hand</span>
          </div>
          <div>
            <p className="text-white/80 text-xs font-medium uppercase tracking-wide">Welcome to OchrePoint</p>
            <h1 className="text-xl font-bold">Let's complete your profile</h1>
          </div>
        </div>
        <p className="text-white/80 text-sm">
          This information helps us serve you better and is used for project documentation and invoicing.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {/* Personal info */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">person</span>
            Contact Information
          </h2>
          <Field label="Your Full Name" required>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="Jane Smith" required className={inputCls} />
          </Field>
          <Field label="Company or Business Name">
            <input type="text" value={form.company} onChange={e => set('company', e.target.value)}
              placeholder="Acme Corp (if applicable)" className={inputCls} />
          </Field>
          <Field label="Phone Number">
            <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
              placeholder="(250) 555-0100" className={inputCls} />
          </Field>
          <Field label="Email Address">
            <input type="email" value={user?.email || ''} disabled
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-100 text-gray-500 cursor-not-allowed" />
          </Field>
        </div>

        {/* Billing address */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">home</span>
            Billing Address
          </h2>
          <Field label="Street Address">
            <input type="text" value={form.address} onChange={e => set('address', e.target.value)}
              placeholder="123 Main Street" className={inputCls} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="City">
                <input type="text" value={form.billing_city} onChange={e => set('billing_city', e.target.value)}
                  placeholder="Duncan" className={inputCls} />
              </Field>
            </div>
            <Field label="Province">
              <select value={form.billing_province} onChange={e => set('billing_province', e.target.value)}
                className={inputCls}>
                <option value="">—</option>
                {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Postal Code">
            <input type="text" value={form.billing_postal} onChange={e => set('billing_postal', e.target.value)}
              placeholder="V9L 1A1" className={`${inputCls} max-w-[140px]`} />
          </Field>
        </div>

        {/* Site address */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">location_on</span>
            Project / Site Address
          </h2>
          <p className="text-xs text-gray-500 -mt-2">Leave blank if the same as billing address above.</p>
          <Field label="Site Address (if different)">
            <textarea value={form.site_address} onChange={e => set('site_address', e.target.value)}
              rows={2} placeholder="456 Oak Lane, Mill Bay, BC, V0R 2P0"
              className={`${inputCls} resize-none`} />
          </Field>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">tune</span>
            Preferences
          </h2>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Preferred Contact Method</label>
            <div className="flex gap-2 flex-wrap">
              {CONTACT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set('preferred_contact', form.preferred_contact === opt.value ? '' : opt.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    form.preferred_contact === opt.value
                      ? 'border-[#C77D3F] bg-[#C77D3F]/10 text-[#C77D3F]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span className="material-symbols-outlined text-base">{opt.icon}</span>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">How did you hear about OchrePoint?</label>
            <select value={form.how_heard} onChange={e => set('how_heard', e.target.value)} className={inputCls}>
              <option value="">Select one…</option>
              {HOW_HEARD_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-3">
          <button type="submit" disabled={busy}
            className="w-full bg-[#C77D3F] text-white rounded-xl py-3.5 text-sm font-bold hover:bg-[#b36e35] disabled:opacity-50 transition-colors shadow-sm">
            {busy ? 'Saving…' : 'Save & Continue to Portal'}
          </button>
          <button type="button" onClick={skip}
            className="w-full text-sm text-gray-400 hover:text-gray-600 py-2">
            Skip for now
          </button>
        </div>
      </form>
    </div>
  );
};
